"use client";

import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
} from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMenu } from "@/lib/menu/MenuProvider";
import { useToast } from "@/lib/toast";
import { MenuItem } from "@/lib/menu/MenuItem";
import { menus, type MenuDef } from "@/lib/menu/menus";
import { useChatNav } from "./ChatShell";
import { DMS, SERVER, type DmEntry, type ServerChannel } from "./data";
import { ServerCard, SERVER_TIP } from "@/lib/menu/Tooltip";
import {
  BellIcon,
  ChevronDownIcon,
  DotsIcon,
  HashIcon,
  MessageIcon,
  NoteIcon,
  PinIcon,
  SearchIcon,
  DashboardIcon,
  IceyIcon,
  UsersIcon,
  XIcon,
} from "./icons";
import { Pfp } from "./Pfp";
import { IconButton } from "./ui";

/* search placeholder cycles these — typed, held, deleted, next */
const SEARCH_PHRASES = [
  "bearcats",
  "goades",
  "cute cats",
  "api secrets",
  "for money",
  "nextdevs",
  "dihs",
];

function Typewriter() {
  const [i, setI] = useState(0);
  const [len, setLen] = useState(0);
  const [del, setDel] = useState(false);

  useEffect(() => {
    const full = SEARCH_PHRASES[i];
    let t: ReturnType<typeof setTimeout>;
    if (!del && len < full.length) t = setTimeout(() => setLen(len + 1), 55);
    else if (!del) t = setTimeout(() => setDel(true), 1700);
    else if (len > 0) t = setTimeout(() => setLen(len - 1), 26);
    else {
      /* brief pause on the empty string before the next phrase starts */
      t = setTimeout(() => {
        setDel(false);
        setI((v) => (v + 1) % SEARCH_PHRASES.length);
      }, 250);
    }
    return () => clearTimeout(t);
  }, [len, del, i]);

  return (
    <>
      {SEARCH_PHRASES[i].slice(0, len)}
      <span
        aria-hidden
        className="ml-px inline-block h-3.5 w-px translate-y-[3px] bg-fg-faint animate-pulse"
      />
    </>
  );
}

/* clickable search bar — opens the search popup (same as Ctrl K),
   the input itself is display-only */
function SearchButton() {
  const { popup } = useMenu();
  return (
    <button
      type="button"
      onClick={() => popup(menus.search)}
      className="flex w-full items-center gap-2 rounded-xl bg-field px-2.5 py-2 ring-1 ring-line transition hover:bg-raise focus:ring-accent/50"
    >
      <SearchIcon className="h-4.5 w-4.5 shrink-0 text-fg-faint" />
      <span className="min-w-0 flex-1 truncate text-left text-title text-fg-faint">
        Search <Typewriter />
      </span>
      <kbd className="shrink-0 rounded bg-raise px-1.5 py-0.5 text-micro font-medium text-fg-faint">
        ⌃K
      </kbd>
    </button>
  );
}

/* icey row snow — hand-scattered flakes that fall through the row on
   hover; each entry sets the flake's lane, size, speed, sway + spin */
const SNOWFLAKES = [
  { left: "8%", size: 6, dur: "2.2s", delay: "-0.4s", drift: "10px", spin: "260deg", op: 0.9 },
  { left: "22%", size: 4, dur: "3.1s", delay: "-1.8s", drift: "-8px", spin: "-220deg", op: 0.5 },
  { left: "38%", size: 7, dur: "2.6s", delay: "-1.1s", drift: "6px", spin: "300deg", op: 0.75 },
  { left: "55%", size: 5, dur: "2.9s", delay: "-2.3s", drift: "-10px", spin: "-260deg", op: 0.6 },
  { left: "68%", size: 8, dur: "2.4s", delay: "-0.9s", drift: "8px", spin: "200deg", op: 0.85 },
  { left: "82%", size: 4, dur: "3.4s", delay: "-2.7s", drift: "-6px", spin: "-300deg", op: 0.45 },
  { left: "93%", size: 6, dur: "2.7s", delay: "-1.5s", drift: "12px", spin: "280deg", op: 0.7 },
];

/* stacked nav row — link when href is set, plain button otherwise */
function NavRow({
  name,
  Icon,
  href,
  mobile,
  glow,
  snow,
}: {
  name: string;
  Icon: ComponentType<{ className?: string }>;
  href?: string;
  mobile?: boolean;
  /* hover background texture — whitened + blue-tinted, screen-blended
     over the row's own hover fill so only the glow shows */
  glow?: string;
  /* falling snowflakes on hover (icey row) */
  snow?: boolean;
}) {
  const nav = useChatNav();
  const cls =
    "group relative flex w-full items-center gap-2.5 overflow-hidden rounded-xl px-2.5 py-2 text-left text-note font-medium text-fg-mute transition hover:bg-raise hover:text-fg";
  const inner = (
    <>
      {glow ? (
        /* isolated blend sandwich — grayscale blob × accent gradient
           (multiply) gives real theme colors with deep shading, then
           screen over the row drops the black field */
        <span
          aria-hidden
          className="absolute inset-0 isolate opacity-0 mix-blend-screen transition-opacity duration-200 group-hover:opacity-100"
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- hover texture */}
          <img
            src={glow}
            alt=""
            className="h-full w-full object-cover"
            style={{ filter: "grayscale(1) contrast(1.15) brightness(1.1)" }}
          />
          <span
            aria-hidden
            className="absolute inset-0 mix-blend-multiply"
            style={{
              background:
                "linear-gradient(135deg, #3d4a9e 0%, var(--color-accent) 55%, var(--color-work) 115%)",
            }}
          />
          {snow ? (
            /* snowfall layer — animation runs regardless, the whole
               glow sandwich fades in on hover so snow's mid-flight.
               masked by the glow image itself so flakes only show on
               the glow, not the bare row. flakes reuse the icey.png
               mask like IceyIcon does */
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 text-fg"
              style={
                {
                  maskImage: `url(${glow})`,
                  WebkitMaskImage: `url(${glow})`,
                  maskSize: "cover",
                  WebkitMaskSize: "cover",
                  maskPosition: "center",
                } as CSSProperties
              }
            >
              {SNOWFLAKES.map((f, i) => (
                <span
                  key={i}
                  className="animate-snowfall absolute top-0"
                  style={
                    {
                      left: f.left,
                      width: f.size,
                      height: f.size,
                      opacity: f.op,
                      animationDuration: f.dur,
                      animationDelay: f.delay,
                      "--drift": f.drift,
                      "--spin": f.spin,
                      background: "currentColor",
                      maskImage: "url(/icey.png)",
                      WebkitMaskImage: "url(/icey.png)",
                      maskSize: "contain",
                      WebkitMaskSize: "contain",
                      maskRepeat: "no-repeat",
                      maskPosition: "center",
                    } as CSSProperties
                  }
                />
              ))}
            </span>
          ) : null}
        </span>
      ) : null}
      <Icon className="relative h-5 w-5 shrink-0" />
      <span className="relative truncate">{name}</span>
    </>
  );
  if (href) {
    return (
      <Link
        href={href}
        onClick={() => {
          /* mobile: route AND slide to the content page */
          if (mobile) nav.goToPage(1);
        }}
        className={cls}
      >
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" className={cls}>
      {inner}
    </button>
  );
}

/* dm list content — `from`/`msg` render as "name: msg" ("you" for self),
   notes rows carry a `note` last-update line instead (DMS lives in
   ./data now — the share sheet reads it too) */

/* dm row — status pfp, "name: msg" preview, rail-style wall line on the
   left edge (short = unread, medium = hover, tall = open), right-click
   context menu, pin glyph, middle-click to close */
function DmRow({
  row,
  mobile,
  pinned,
  unread,
  muted,
  onCtx,
  onAskClose,
}: {
  row: DmEntry;
  mobile: boolean;
  pinned: boolean;
  unread: boolean;
  muted: boolean;
  onCtx: (x: number, y: number) => void;
  onAskClose: () => void;
}) {
  const nav = useChatNav();
  const active = usePathname() === row.href;
  /* mobile long-press — 650ms hold opens the context menu at the finger;
     set once it fires so the release tap doesn't also navigate */
  const hold = useRef<{ t: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  const held = useRef(false);
  const clearHold = () => {
    if (hold.current) {
      clearTimeout(hold.current.t);
      hold.current = null;
    }
  };
  const line = active
    ? "h-9"
    : unread
      ? "h-2 group-hover:h-5"
      : "h-0 group-hover:h-5";
  return (
    <li className="group/row relative">
      <Link
        href={row.href}
        onClick={(e) => {
          if (held.current) {
            /* the tap that ended a long-press — the menu is already
               open, don't navigate */
            e.preventDefault();
            held.current = false;
            return;
          }
          /* mobile: route AND slide to the chat page — stays there
             until the user swipes back to the nav page */
          if (mobile) nav.goToPage(1);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          onCtx(e.clientX, e.clientY);
        }}
        onPointerDown={(e) => {
          if (!mobile || e.pointerType !== "touch") return;
          const x = e.clientX, y = e.clientY;
          hold.current = {
            x, y,
            t: setTimeout(() => {
              hold.current = null;
              held.current = true;
              onCtx(x, y);
            }, 650),
          };
        }}
        onPointerMove={(e) => {
          /* scrolls / drags kill the hold — 12px of wiggle allowed */
          const h = hold.current;
          if (h && (Math.abs(e.clientX - h.x) > 12 || Math.abs(e.clientY - h.y) > 12))
            clearHold();
        }}
        onPointerUp={clearHold}
        onPointerCancel={clearHold}
        onAuxClick={(e) => {
          /* middle-click asks to close the dm like the hover X */
          if (e.button === 1) {
            e.preventDefault();
            onAskClose();
          }
        }}
        className="group relative flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition hover:bg-raise"
      >
        <span
          aria-hidden
          className={`absolute top-1/2 -left-2 w-[5px] -translate-y-1/2 rounded-r-md bg-accent-2 transition-[height] duration-150 ${line}`}
        />
        <Pfp src={row.src} size={40} rounded="rounded-xl" status={row.status} />
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-note font-semibold ${muted ? "text-fg-faint" : "text-fg"}`}
          >
            {row.name}
          </span>
          {row.hover ? (
            /* hover roll — the message line slides up + out while the
               hover line rolls up from below, like discord's status */
            <span className="relative block h-3.5 overflow-hidden text-micro leading-3.5 text-fg-mute">
              <span className="absolute inset-x-0 truncate transition-all duration-200 ease-out group-hover:-translate-y-3.5 group-hover:opacity-0">
                {row.note ?? `${row.from}: ${row.msg}`}
              </span>
              <span className="absolute inset-x-0 translate-y-3.5 truncate opacity-0 transition-all duration-200 ease-out group-hover:translate-y-0 group-hover:opacity-100">
                {row.hover}
              </span>
            </span>
          ) : (
            <span className="block truncate text-micro text-fg-mute">
              {row.note ?? `${row.from}: ${row.msg}`}
            </span>
          )}
        </span>
        {pinned ? (
          <PinIcon className="h-3 w-3 shrink-0 text-fg-faint group-hover/row:hidden" />
        ) : null}
      </Link>
      {/* hover X — asks to close; sibling of the link so there's no
          nested-interactive. group/row keeps it visible while it's the
          thing being hovered */}
      <button
        type="button"
        aria-label={`Close ${row.name}`}
        onClick={onAskClose}
        className="absolute top-1/2 right-1.5 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md bg-slab text-fg-faint opacity-0 transition hover:bg-lift hover:text-fg group-hover/row:opacity-100 focus-visible:opacity-100"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

/* channel row — # glyph + name, same wall-line anatomy as DmRow
   (tall = open, medium = hover) */
function ChanRow({ c, mobile }: { c: ServerChannel; mobile: boolean }) {
  const nav = useChatNav();
  const href = `/s/${SERVER.id}/${c.id}`;
  const active = usePathname() === href;
  return (
    <li className="group relative">
      <Link
        href={href}
        onClick={() => {
          /* mobile: route AND slide to the chat page */
          if (mobile) nav.goToPage(1);
        }}
        className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-note font-medium transition hover:bg-raise hover:text-fg ${
          active ? "bg-raise text-fg" : "text-fg-mute"
        }`}
      >
        <span
          aria-hidden
          className={`absolute top-1/2 -left-2 w-[5px] -translate-y-1/2 rounded-r-md bg-accent-2 transition-[height] duration-150 ${
            active ? "h-9" : "h-0 group-hover:h-5"
          }`}
        />
        <HashIcon className="h-4.5 w-4.5 shrink-0 text-fg-faint" />
        <span className="min-w-0 truncate">{c.name}</span>
      </Link>
    </li>
  );
}

/* the dm bar's server swap — on /s/* routes the column shows the
   server's channels instead of the dm list; the name header opens the
   same server card the rail tile's mobile tap shows */
function ServerColumn({ mobile }: { mobile: boolean }) {
  const { popup } = useMenu();
  return (
    <>
      <div className="px-2 pt-3">
        <button
          type="button"
          onClick={() =>
            popup({
              id: "server-card",
              title: SERVER.name,
              Content: () => <ServerCard def={SERVER_TIP} />,
              mobileCard: true,
              bare: true,
            })
          }
          className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-note font-semibold text-fg transition hover:bg-raise"
        >
          <span
            aria-hidden
            className="h-6 w-6 shrink-0 rounded-md bg-cover bg-center"
            style={{ backgroundImage: `url(${SERVER.src})` }}
          />
          <span className="min-w-0 flex-1 truncate">{SERVER.name}</span>
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-fg-faint" />
        </button>
      </div>

      {/* divider row — line, "Channels" label, line */}
      <div className="mx-5 mt-3 flex items-center gap-2.5">
        <span aria-hidden className="h-px w-4 shrink-0 bg-line" />
        <span className="text-micro font-semibold tracking-[0.14em] text-fg-faint uppercase">
          Channels
        </span>
        <span aria-hidden className="h-px min-w-0 flex-1 bg-line" />
      </div>

      <ul className="mt-1 min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 scroll-dark">
        {SERVER.channels.map((c) => (
          <ChanRow key={c.id} c={c} mobile={mobile} />
        ))}
      </ul>
    </>
  );
}

export function DmBar({ mobile = false }: { mobile?: boolean }) {
  const { openAt, confirm } = useMenu();
  const toast = useToast();
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [muted, setMuted] = useState<Set<string>>(new Set());
  const nav = useChatNav();
  const pathname = usePathname();
  /* /s/* routes swap the dm list for the server's channel column */
  const inServer = pathname.startsWith("/s/");

  /* every "close" path (hover X, middle-click, menu) asks first —
     leaving a group gets its own dialog with a silent option */
  const askClose = (row: DmEntry) =>
    row.id === "gc"
      ? confirm({
          title: "Are you sure you want to leave this group?",
          actions: [
            {
              label: "Yes",
              tone: "accent",
              onClick: () => {
                nav.closeDm(row.id);
                toast.alert({
                  kind: "warning",
                  title: "You left The GC",
                  desc: "It stays gone until someone re-adds you.",
                });
              },
            },
            {
              label: "Yes, silently",
              onClick: () => nav.closeDm(row.id),
            },
          ],
          foot: { label: "Cancel" },
        })
      : row.id === "notes"
        ? confirm({
            title: "Archive or delete your notes?",
            body: "Deleting also destroys the notes encryption token, so they can never be recovered. Archiving only locks you out of editing until you unarchive.",
            danger: true,
            actions: [
              {
                label: "Archive",
                tone: "accent",
                icon: PinIcon,
                onClick: () => {
                  nav.closeDm(row.id);
                  toast.top("Notes archived", NoteIcon);
                },
              },
              {
                label: "Delete",
                icon: XIcon,
                onClick: () => {
                  nav.closeDm(row.id);
                  toast.popup({
                    kind: "error",
                    title: "Notes deleted",
                    desc: "The encryption token was destroyed — they can't be recovered.",
                  });
                },
              },
            ],
            foot: { label: "Cancel" },
          })
        : confirm({
          title: `Close DM with ${row.name}?`,
          body: "The conversation will be hidden from your DM list.",
          actions: [
            {
              label: "Close DM",
              tone: "accent",
              icon: XIcon,
              onClick: () => {
                nav.closeDm(row.id);
                toast.top(`Closed DM with ${row.name}`, XIcon);
              },
            },
            { label: "Cancel", tone: "subtle" },
          ],
        });

  const flip = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  /* right-click menu on a dm row — everything actually does something */
  const dmCtx = (row: DmEntry): MenuDef => ({
    id: `dm-ctx-${row.id}`,
    title: row.name,
    tab: "DM",
    Content: () => (
      <>
        {row.unread && !readIds.has(row.id) ? (
          <MenuItem
            icon="message"
            onClick={() => {
              setReadIds((s) => new Set(s).add(row.id));
              toast.top("Marked as read", MessageIcon);
            }}
          >
            Mark as read
          </MenuItem>
        ) : null}
        <MenuItem
          icon="pin"
          onClick={() => {
            setPinned((s) => flip(s, row.id));
            toast.top(
              pinned.has(row.id)
                ? "Unpinned conversation"
                : "Pinned conversation",
              PinIcon,
            );
          }}
        >
          {pinned.has(row.id) ? "Unpin" : "Pin"} conversation
        </MenuItem>
        <MenuItem
          icon="bell"
          onClick={() => {
            setMuted((s) => flip(s, row.id));
            toast.top(
              `${muted.has(row.id) ? "Unmuted" : "Muted"} ${row.name}`,
              BellIcon,
            );
          }}
        >
          {muted.has(row.id) ? "Unmute" : "Mute"} {row.name}
        </MenuItem>
        <MenuItem icon="x" onClick={() => askClose(row)}>
          {row.id === "gc" ? "Leave" : "Close"}{" "}
          {row.id === "gc" ? "group" : row.id === "notes" ? "notes" : "DM"}
        </MenuItem>
      </>
    ),
  });

  const rows = DMS.filter((r) => !nav.closedDms.has(r.id)).sort(
    /* pinned first, stable within each group */
    (a, b) => Number(pinned.has(b.id)) - Number(pinned.has(a.id)),
  );

  /* Ctrl Shift K → new message (focus the composer). Ctrl N itself is a
     browser-level shortcut that can't be intercepted. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || !e.shiftKey || e.altKey) return;
      if (e.key.toLowerCase() === "k") {
        e.preventDefault();
        document.getElementById("composer")?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <aside
      aria-label={inServer ? "Server channels" : "Direct messages"}
      className={
        mobile
          ? "mt-[3px] ml-[3px] flex min-w-0 flex-1 flex-col rounded-tl-[var(--card-r)] bg-slab pb-20 text-fg"
          : "mt-[var(--matte-t)] flex w-[var(--dmbar-w)] shrink-0 flex-col rounded-tl-[var(--card-r)] bg-slab pb-[var(--profile-overlap)] text-fg"
      }
    >
      {inServer ? (
        <ServerColumn mobile={mobile} />
      ) : (
        <>
      <div className="px-5 pt-4">
        <SearchButton />
      </div>

      {/* Friends / Shop / Icey — stacked nav rows */}
      <div className="mt-3 space-y-0.5 px-2">
        <NavRow name="Friends" Icon={UsersIcon} href="/app/friends" mobile={mobile} />
        <NavRow name="Dashboard" Icon={DashboardIcon} href="/app/" mobile={mobile} />
        <NavRow
          name="Icey"
          Icon={IceyIcon}
          glow="/icey-glow.png"
          snow
        />
      </div>

      {/* divider row — line, "DMs" label, line, options menu */}
      <div className="mx-5 mt-3 flex items-center gap-2.5">
        <span aria-hidden className="h-px w-4 shrink-0 bg-line" />
        <span className="text-micro font-semibold tracking-[0.14em] text-fg-faint uppercase">
          DMs
        </span>
        <span aria-hidden className="h-px min-w-0 flex-1 bg-line" />
        <IconButton
          label="DM options"
          size="xs"
          tip={{ title: "Options", icon: DotsIcon }}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            openAt(menus.dms, r.right - 216, r.bottom + 6);
          }}
        >
          <DotsIcon className="h-4 w-4" />
        </IconButton>
      </div>

      <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 scroll-dark">
        {rows.map((row) => (
          <DmRow
            key={row.id}
            row={row}
            mobile={mobile}
            pinned={pinned.has(row.id)}
            muted={muted.has(row.id)}
            unread={Boolean(row.unread) && !readIds.has(row.id) && !muted.has(row.id)}
            onCtx={(x, y) => openAt(dmCtx(row), x, y)}
            onAskClose={() => askClose(row)}
          />
        ))}
      </ul>
        </>
      )}
    </aside>
  );
}
